import { skipToken } from '@reduxjs/toolkit/query';
import StatusChip from 'components/@extended/StatusChip';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { DatePickerField } from 'components/forms/inputs/common/DatePickerField';
import { LineTab, LineTabs, LineTabsList } from 'components/LineTabs';
import Loader from 'components/Loader';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { format, parseISO } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { HourlyRatesEditForm } from 'pages/pricelists/modal/HourlyRatesEditForm';
import { PricelistsCarousel } from 'pages/pricelists/modal/PricelistsCarousel';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'store';
import { useGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useGetJobRolesListQuery } from 'store/api/jobRoles';
import {
    APIMattersSlice,
    useCreateMatterPricelistMutation,
    useDeleteMatterPricelistMutation,
    useUpdateMatterPricelistHeaderMutation,
    useUpdateMatterPricelistMutation,
    useUpdateMatterPricingKindMutation,
    useUpdateMatterPricingLinkMutation,
} from 'store/api/matters';
import { useGetPricelistByIdQuery } from 'store/api/pricelists';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features';
import { JobRolesListElement } from 'types/jobRoles';
import { PricelistReference, PricelistTabDraft, UpdateHourlyRatesDto } from 'types/matters';
import { NamedEntityMinDto } from 'types/other';
import { CalculatedPricelist, MatterFollowedPricelist, MatterPricelist, PricingKind } from 'types/pricelists';
import { errorAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { DEFAULT_DATE_FORMAT_PATTERN } from 'utils/dateFormat';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { CaseFollowPane } from './CaseFollowPane';
import {
    adoptStoredIds,
    baseTabWithRates,
    buildRoleRows,
    computeSaveOperations,
    hasStagedPricing,
    hasValidationErrors,
    isEmptyListOfKindSwitch,
    nextNewTabKey,
    ownCopyTabs,
    SaveOperation,
    sortTabs,
    StagedPricing,
    switchStagedKind,
    tabFromList,
    tabLabel,
    tabsFromPeriods,
    validateTab,
} from './matterPricelistEditorUtils';
import { PersonalRatesLedger, RateCandidate } from './PersonalRatesLedger';
import { PricingKindSwitch } from './PricingKindSwitch';
import { useCaseRepricingConfirmation } from './useCaseRepricingConfirmation';
import { useToday } from './useEmployeePricing';

// What the case form takes back from the editor: every tab, sorted, the referenced template and the followed list.
export interface StagedPricelists {
    referencedPricelist: PricelistReference;
    tabs: PricelistTabDraft[];
    followsPricelist: MatterFollowedPricelist | null;
    // The tabs are the own copy of the list the case followed, unedited: the save leaves the lists to the copy the
    // server makes as it ends the following, which holds the followed prices as they are then, not as loaded here.
    keepsServerCopy: boolean;
}

// The case's kind and link as the editor stages them, with what an undo restores: the state before the first kind
// switch, and the state before the first follow picked in this edit.
interface LinkState {
    pricingKind: PricingKind;
    followsPricelist: MatterFollowedPricelist | null;
    beforeSwitch: StagedPricing | null;
    beforeFollow: StagedPricing | null;
    copiedFrom: string | null;
    // The tabs an own copy started from, to tell whether they were edited since.
    copyBaseline: PricelistTabDraft[] | null;
}

function followedOf(pricelist: CalculatedPricelist): MatterFollowedPricelist {
    return {
        id: pricelist.id,
        name: pricelist.name,
        scopeTypeCode: pricelist.scopeTypeCode,
        contractId: pricelist.contractId ?? null,
        modelPricelist: null,
    };
}

interface CommonProps {
    clientParty?: NamedEntityMinDto;
    currencyTypeCode?: string | null;
    serviceAgreementContractId?: number | null;
    classifiers: Record<string, ClassifierData>;
    openedDate: string | null;
    // The case's team, responsible and supervising employee; anyone priced outside it is marked as such.
    caseTeam: NamedEntityMinDto[];
    referencedPricelist: PricelistReference | null;
    pricingKind: PricingKind;
    followsPricelist: MatterFollowedPricelist | null;
    onClose: () => void;
}

// The case form stages every list until the case save: Apply hands the tabs back, nothing is sent.
interface FormModeProps extends CommonProps {
    mode: 'form';
    // A stored case's id, for the hours a follow re-prices; absent while the case is created.
    matterId?: number;
    tabs: PricelistTabDraft[];
    onApply: (value: StagedPricelists) => void;
}

// The case page commits every changed tab on Save, one scoped call per list, then refetches once.
interface PageModeProps extends CommonProps {
    mode: 'page';
    matterId: number;
    lists: MatterPricelist[];
    // A kind switch is a write of its own, so only the case page offers it, to those who may edit the case.
    canSwitchKind: boolean;
    onSaved: () => void;
}

type Props = FormModeProps | PageModeProps;

const MODAL_STYLE = { width: 'min(1040px, 95vw)' };
const ACTIVE_EMPLOYEE_PAGE_SIZE = 2000;

export const MATTER_PRICELIST_EDITOR_MODAL_STYLE = MODAL_STYLE;

const toDate = (value: string | null): Date | null => (value ? parseISO(value) : null);

// The staged or stored lists as editable tabs, every role in the grid; a case without a list still gets the
// base tab, which the first save creates.
const initialTabs = (lists: PricelistTabDraft[], jobRoles: JobRolesListElement[]): PricelistTabDraft[] => {
    const tabs = lists.some((tab) => tab.isBase) ? lists : [baseTabWithRates(), ...lists];
    return tabs.map((tab) => ({ ...cloneDeep(tab), rates: buildRoleRows(jobRoles, tab.rates) }));
};

export const MatterPricelistEditorModal = (props: Props): JSX.Element => {
    const { clientParty, currencyTypeCode, serviceAgreementContractId, classifiers, openedDate, caseTeam, onClose } =
        props;
    const dateFormat = useEffectiveDateFormat();
    const today = useToday();
    const isEmployeePricingEnabled = useIsFeatureEnabled(FeatureFlag.EMPLOYEE_PRICING);
    const tenantKind = isEmployeePricingEnabled ? PricingKind.EMPLOYEE : PricingKind.ROLE;
    const { confirmKindSwitch, confirmFollow } = useCaseRepricingConfirmation(props.matterId);
    const { data: jobRoles, isLoading: isLoadingJobRoles } = useGetJobRolesListQuery();
    const caseTeamIds = useMemo(() => new Set(caseTeam.map((person) => person.id)), [caseTeam]);
    const { data: activeEmployeeList } = useGetEmployeesListWithFiltersQuery({
        page: 0,
        pageSize: ACTIVE_EMPLOYEE_PAGE_SIZE,
    });
    const activeEmployees = useMemo<RateCandidate[] | undefined>(
        () =>
            activeEmployeeList?.content
                .map((employee) => ({
                    id: employee.id,
                    name: employee.name,
                    jobRole:
                        (jobRoles ?? []).find((jobRole) => jobRole.id === employee.latestEmployment?.jobRoleId) ?? null,
                }))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [activeEmployeeList, jobRoles]
    );

    const [tabs, setTabs] = useState<PricelistTabDraft[] | null>(null);
    const [activeKey, setActiveKey] = useState<string>('base');
    const [referencedPricelist, setReferencedPricelist] = useState<PricelistReference>({
        id: props.referencedPricelist?.id ?? null,
        pricingHash: props.referencedPricelist?.pricingHash,
    });
    const [link, setLink] = useState<LinkState>({
        pricingKind: props.pricingKind,
        followsPricelist: props.followsPricelist,
        beforeSwitch: null,
        beforeFollow: null,
        copiedFrom: null,
        copyBaseline: null,
    });
    const [isValidating, setIsValidating] = useState(false);
    const [isLoadingPicker, setIsLoadingPicker] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Initialised once the job roles are in, with a render-time guard as the shared modal does.
    if (!isLoadingJobRoles && jobRoles && !tabs) {
        const initial = initialTabs(props.mode === 'form' ? props.tabs : props.lists.map(tabFromList), jobRoles);
        setTabs(initial);
        const inForce = initial.reduce<PricelistTabDraft>(
            (current, tab) => (tab.validFrom === null || tab.validFrom <= today ? tab : current),
            initial[0]
        );
        setActiveKey(inForce.key);
    }

    const [updateMatterPricelist] = useUpdateMatterPricelistMutation();
    const [createMatterPricelist] = useCreateMatterPricelistMutation();
    const [updateMatterPricelistHeader] = useUpdateMatterPricelistHeaderMutation();
    const [deleteMatterPricelist] = useDeleteMatterPricelistMutation();
    const [updateMatterPricingKind] = useUpdateMatterPricingKindMutation();
    const [updateMatterPricingLink] = useUpdateMatterPricingLinkMutation();
    const dispatch = useDispatch();

    const activeTab = tabs?.find((tab) => tab.key === activeKey) ?? tabs?.[0];
    const perEmployee = link.pricingKind === PricingKind.EMPLOYEE;
    const isStoredLink =
        !!link.followsPricelist && link.followsPricelist.id === props.followsPricelist?.id && !link.beforeFollow;
    // A stored case that follows the list holds its periods as its own lists; a list picked in this edit, or the
    // one a new case is to follow, is shown from its own periods until the save makes the copy.
    const holdsCopy = isStoredLink && props.matterId !== undefined;
    const pickedFollowId = link.followsPricelist && !holdsCopy ? link.followsPricelist.id : undefined;
    const { data: pickedFollowDetail, isFetching: isFetchingPickedFollow } = useGetPricelistByIdQuery(
        pickedFollowId ?? skipToken
    );
    const followedTabs = useMemo(
        () => (pickedFollowId ? tabsFromPeriods(pickedFollowDetail?.periods) : (tabs ?? [])),
        [pickedFollowId, pickedFollowDetail, tabs]
    );
    const kindChoices =
        props.mode === 'page' && props.canSwitchKind && props.pricingKind !== tenantKind
            ? [props.pricingKind, tenantKind]
            : null;

    const snapshot = (): StagedPricing => ({
        pricingKind: link.pricingKind,
        followsPricelist: link.followsPricelist,
        referencedPricelist,
        tabs: tabs ?? [],
    });

    const restore = (staged: StagedPricing) => {
        setTabs(
            staged.pricingKind === PricingKind.ROLE
                ? staged.tabs.map((tab) => ({ ...tab, rates: buildRoleRows(jobRoles ?? [], tab.rates) }))
                : staged.tabs
        );
        setReferencedPricelist(staged.referencedPricelist);
        setActiveKey(staged.tabs[0]?.key ?? 'base');
    };

    const onKindChange = (pricingKind: PricingKind) => {
        const apply = () => {
            const { staged, beforeSwitch } = switchStagedKind(snapshot(), pricingKind, link.beforeSwitch);
            restore(staged);
            setLink({
                pricingKind: staged.pricingKind,
                followsPricelist: staged.followsPricelist,
                beforeSwitch,
                beforeFollow: null,
                copiedFrom: null,
                copyBaseline: null,
            });
        };
        if (pricingKind === props.pricingKind) {
            apply();
        } else {
            void confirmKindSwitch(pricingKind, apply);
        }
    };

    // What the case holds as the editor opens; a follow then discards it.
    const [storedHasPricing] = useState(() =>
        hasStagedPricing(props.mode === 'form' ? props.tabs : props.lists.map(tabFromList))
    );

    // An employee-priced case keeps no referenced list once it follows a list or keeps its own copy.
    const dropReferencedList = () => {
        if (perEmployee) setReferencedPricelist((current) => ({ ...current, id: null }));
    };

    const onFollow = (pricelist: CalculatedPricelist) => {
        if (link.followsPricelist?.id === pricelist.id) return;
        const apply = () => {
            setLink((current) => ({
                ...current,
                followsPricelist: followedOf(pricelist),
                beforeFollow: current.beforeFollow ?? snapshot(),
                copiedFrom: null,
                copyBaseline: null,
            }));
            dropReferencedList();
        };
        // Following discards the case's own prices, or the list it followed, and re-prices its uninvoiced work.
        if (props.followsPricelist || storedHasPricing) {
            void confirmFollow(pricelist.name, apply);
        } else {
            apply();
        }
    };

    const onUndoFollow = () => {
        if (!link.beforeFollow) return;
        restore(link.beforeFollow);
        setLink((current) => ({
            ...current,
            followsPricelist: current.beforeFollow?.followsPricelist ?? null,
            beforeFollow: null,
        }));
    };

    // Nothing bills differently: the followed prices up to today stay as the case's own lists.
    const onMakeOwnCopy = () => {
        if (!link.followsPricelist) return;
        const kept = ownCopyTabs(followedTabs, today);
        const copy = kept.map((tab) => ({ ...tab, rates: buildRoleRows(jobRoles ?? [], tab.rates) }));
        setTabs(copy);
        setActiveKey((kept.find((tab) => tab.validFrom !== null && tab.validFrom <= today) ?? kept[0])?.key ?? 'base');
        setLink((current) => ({
            ...current,
            followsPricelist: null,
            copiedFrom: current.followsPricelist?.name ?? null,
            copyBaseline: copy,
        }));
        dropReferencedList();
    };

    const updateActiveTab = useCallback(
        (change: Partial<PricelistTabDraft>) => {
            setTabs((current) =>
                current ? current.map((tab) => (tab.key === activeKey ? { ...tab, ...change } : tab)) : current
            );
        },
        [activeKey]
    );

    const onPrefill = useCallback(
        (value: UpdateHourlyRatesDto) => {
            // The picker merges role prices into the open tab; the ledger is this case's own state and stays.
            // It stays open on a choice: the prices land beside it, so a wrong pick is one click to correct.
            setReferencedPricelist(value.referencedPricelist);
            updateActiveTab({ rates: value.rates });
        },
        [updateActiveTab]
    );

    const addTab = () => {
        if (!activeTab || !tabs) return;
        const key = nextNewTabKey(tabs);
        setTabs((current) => [
            ...(current ?? []),
            {
                key,
                pricingKind: link.pricingKind,
                id: null,
                isBase: false,
                validFrom: null,
                name: null,
                rates: cloneDeep(activeTab.rates),
                personalRates: cloneDeep(activeTab.personalRates),
            },
        ]);
        setActiveKey(key);
    };

    const removeTab = (key: string) => {
        if (!tabs) return;
        const target = tabs.find((tab) => tab.key === key);
        if (!target || target.isBase) return;
        const remaining = tabs.filter((tab) => tab.key !== key);
        setTabs(remaining);
        if (activeKey === key) {
            setActiveKey(remaining[Math.max(0, tabs.indexOf(target) - 1)].key);
        }
    };

    const label = useCallback(
        (tab: PricelistTabDraft) =>
            tabLabel(
                tab,
                openedDate,
                (date) => format(parseISO(date), dateFormat),
                (date) => t('pricelists.case-editor.from-date', { date }),
                t('pricelists.case-editor.new-pricelist')
            ),
        [openedDate, dateFormat]
    );

    // A follower's lists are the followed list's, so there is nothing of its own to check.
    const firstInvalidTab = (drafts: PricelistTabDraft[]): PricelistTabDraft | undefined =>
        link.followsPricelist || isEmptyListOfKindSwitch(drafts, link.pricingKind, props.pricingKind)
            ? undefined
            : drafts.find((tab) => hasValidationErrors(validateTab(tab, drafts, openedDate, link.pricingKind)));

    // The followed list may have moved since the editor opened: an own copy nobody edited takes the server's.
    const isUneditedCopy = (staged: PricelistTabDraft[]): boolean =>
        !link.followsPricelist && !!link.copyBaseline && isEqual(staged, link.copyBaseline);

    const onApply = () => {
        if (!tabs || props.mode !== 'form' || isFetchingPickedFollow) return;
        setIsValidating(true);
        const invalid = firstInvalidTab(tabs);
        if (invalid) {
            setActiveKey(invalid.key);
            errorAlert(t('validation.required-fields-empty'));
            return;
        }
        props.onApply({
            referencedPricelist,
            tabs: link.followsPricelist ? followedTabs : sortTabs(tabs),
            followsPricelist: link.followsPricelist,
            keepsServerCopy: isUneditedCopy(tabs),
        });
    };

    /**
     * The kind switch and the link go first, each a write of its own, and the lists' edits are then computed against
     * what the server holds after them. Null when the case now follows a list, which leaves no list edit to make.
     */
    const commitKindAndLink = async (
        matterId: string,
        staged: PricelistTabDraft[]
    ): Promise<{ stored: PricelistTabDraft[]; staged: PricelistTabDraft[] } | null> => {
        if (props.mode !== 'page') return null;
        const kindChanged = link.pricingKind !== props.pricingKind;
        let stored = props.lists.map(tabFromList);
        let tabsToSave = staged;
        if (kindChanged) {
            const lists = await updateMatterPricingKind({ id: matterId, pricingKind: link.pricingKind }).unwrap();
            stored = lists.map(tabFromList);
            tabsToSave = adoptStoredIds(staged, stored);
        }
        if (link.followsPricelist) {
            if (kindChanged || !isStoredLink) {
                await updateMatterPricingLink({
                    id: matterId,
                    followsPricelist: { id: link.followsPricelist.id },
                }).unwrap();
            }
            return null;
        }
        if (!kindChanged && props.followsPricelist) {
            const lists = await updateMatterPricingLink({ id: matterId, followsPricelist: null }).unwrap();
            stored = lists.map(tabFromList);
            tabsToSave = isUneditedCopy(staged) ? stored : adoptStoredIds(staged, stored);
        }
        return { stored, staged: tabsToSave };
    };

    const runOperation = async (matterId: string, operation: SaveOperation): Promise<number | void> => {
        switch (operation.type) {
            case 'delete':
                return deleteMatterPricelist({ id: matterId, pricelistId: operation.pricelistId }).unwrap();
            case 'create':
                return createMatterPricelist({
                    id: matterId,
                    sourcePricelistId: null,
                    validFrom: operation.validFrom,
                    name: operation.name,
                    rates: operation.rates,
                    personalRates: operation.personalRates,
                }).unwrap();
            case 'rows':
                return updateMatterPricelist({
                    id: matterId,
                    referencedPricelist,
                    pricelistId: operation.pricelistId,
                    rates: operation.rates ?? undefined,
                    personalRates: operation.personalRates,
                })
                    .unwrap()
                    .then(() => undefined);
            case 'header':
                return updateMatterPricelistHeader({
                    id: matterId,
                    pricelistId: operation.pricelistId,
                    validFrom: operation.validFrom,
                    name: operation.name,
                }).unwrap();
        }
    };

    const onSave = async () => {
        if (!tabs || !jobRoles || props.mode !== 'page') return;
        setIsValidating(true);
        const invalid = firstInvalidTab(tabs);
        if (invalid) {
            setActiveKey(invalid.key);
            errorAlert(t('validation.required-fields-empty'));
            return;
        }
        const matterId = String(props.matterId);
        setIsSaving(true);
        let failed = false;
        try {
            // The request's own error toast says why a kind switch or a link change failed.
            const committed = await commitKindAndLink(matterId, tabs).catch(() => {
                failed = true;
                return null;
            });
            const operations = committed ? computeSaveOperations(committed.stored, committed.staged, label) : [];
            const templateChanged =
                (props.referencedPricelist?.id ?? null) !== (referencedPricelist.id ?? null) ||
                (props.referencedPricelist?.pricingHash ?? null) !== (referencedPricelist.pricingHash ?? null);
            if (
                committed &&
                templateChanged &&
                !operations.some((operation) => operation.type === 'rows' && operation.pricelistId === null)
            ) {
                operations.push({ type: 'rows', pricelistId: null, rates: null, personalRates: null, label: '' });
            }
            for (const operation of operations) {
                try {
                    const result = await runOperation(matterId, operation);
                    if (operation.type === 'create' && typeof result === 'number') {
                        // The tab is now a stored list: a retry after a later failure must not create it twice.
                        setTabs((current) =>
                            current
                                ? current.map((tab) => (tab.key === operation.key ? { ...tab, id: result } : tab))
                                : current
                        );
                    }
                } catch (error) {
                    errorAlert(
                        t('pricelists.case-editor.save-failed', {
                            name: operation.label,
                            message: getApiErrorMessage(error) ?? t('forms.error-submitting-the-form'),
                        })
                    );
                    failed = true;
                    break;
                }
            }
        } finally {
            // One rates refetch for the whole Save, after the last call: the writes themselves do not touch
            // the rates read, because a refetch still pending from an earlier call would drop the one a
            // later call asks for. Also after a failure, so the page shows what did get stored.
            dispatch(APIMattersSlice.util.invalidateTags([{ type: 'MatterRates', id: matterId }]));
            props.onSaved();
            setIsSaving(false);
        }
        if (!failed) {
            onClose();
        }
    };

    if (isLoadingJobRoles || !tabs || !activeTab) {
        return <Loader />;
    }

    const validation = validateTab(activeTab, tabs, openedDate, link.pricingKind);
    const dateErrorText = (): string | null => {
        if (!isValidating || !validation.dateError) return null;
        switch (validation.dateError) {
            case 'required':
                return t('pricelists.case-editor.date-required');
            case 'not-after-opening':
                return t('pricelists.case-editor.date-not-after-opening');
            case 'duplicate':
                return t('pricelists.case-editor.date-duplicate');
        }
    };

    const title = clientParty
        ? t('pricelists.case-editor.title-for-client', { client: clientParty.name })
        : t('pricelists.case-editor.title');

    return (
        <>
            <ModalHeader title={title} onCancel={onClose} />
            {/* The modal Card is a capped flex column: the body is the part that shrinks, and each pane
                scrolls on its own so the role grid and the ledger stay in one scroll. */}
            <div className="flex min-h-0 flex-1">
                {/* What the pane is for sits above the scroll, so it stays put while the cards move; the
                    sections below name themselves, which is why this pane carries no heading of its own.
                    Only the two divs are flex children here — the cards stay block-level inside the
                    scroller, where their own min-height still sizes them. */}
                <div className="flex w-1/2 min-w-0 flex-col border-r border-zinc-300">
                    <div className="text-grey-500 new:text-caption px-4.5 pt-3.5 pb-2 text-[0.72rem]">
                        {t(
                            perEmployee
                                ? 'pricelists.case-editor.picker-intro-follow'
                                : 'pricelists.case-editor.picker-intro'
                        )}
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto px-4.5 pb-3.5">
                        {/* Remounted on a kind switch: its picked list belongs to the kind it was picked under. */}
                        <PricelistsCarousel
                            key={link.pricingKind}
                            pricelistData={{ referencedPricelist, rates: activeTab.rates }}
                            onChange={onPrefill}
                            clientId={clientParty?.id}
                            currencyTypeCode={currencyTypeCode}
                            serviceAgreementContractId={serviceAgreementContractId}
                            setIsLoadingData={setIsLoadingPicker}
                            pricingKind={link.pricingKind}
                            followedPricelistId={link.followsPricelist?.id ?? null}
                            onFollow={onFollow}
                        />
                    </div>
                </div>

                <div className="min-w-0 flex-1 overflow-y-auto px-4.5 py-3.5">
                    {kindChoices ? (
                        <div className="pb-3">
                            <PricingKindSwitch value={link.pricingKind} kinds={kindChoices} onChange={onKindChange} />
                        </div>
                    ) : perEmployee ? (
                        <div className="flex items-center gap-2 pb-3 text-[0.8rem]">
                            <span className="text-grey-700 font-semibold">
                                {t('pricelists.case-follow.kind-label')}
                            </span>
                            <StatusChip type="neutral" label={t('pricelists.case-follow.kind-employee')} />
                        </div>
                    ) : null}
                    {link.followsPricelist ? (
                        <CaseFollowPane
                            followed={link.followsPricelist}
                            tabs={followedTabs}
                            openedDate={openedDate}
                            currencyTypeCode={currencyTypeCode}
                            onMakeOwnCopy={isStoredLink ? onMakeOwnCopy : undefined}
                            onKeepOwnPrices={link.beforeFollow ? onUndoFollow : undefined}
                        />
                    ) : (
                        <div className="flex w-full flex-col">
                            <div className="flex items-center justify-between gap-3 pb-1.5">
                                <span className="text-[0.85rem] font-semibold">{t('pricelists.hourly-rates')}</span>
                            </div>

                            <div className="flex min-w-0 items-center gap-3 border-b border-zinc-300/60">
                                <LineTabs
                                    value={activeTab.key}
                                    onValueChange={(key) => setActiveKey(String(key))}
                                    className="min-w-0"
                                >
                                    {/* Label and delete only: the Scheduled word belongs to the read-only
                                    popover, and carrying it here widened every dated tab until the strip
                                    scrolled and showed fragments of names instead of names. The open tab
                                    states its date in "Applies from" directly below. The wrapper draws the
                                    border under the strip and the add button together. */}
                                    <LineTabsList arrows={{ back: t('back'), next: t('next') }} className="border-b-0">
                                        {tabs.map((tab) => (
                                            <LineTab
                                                key={tab.key}
                                                value={tab.key}
                                                size="compact"
                                                reserveSelectedWidth={false}
                                            >
                                                <span className="flex items-center gap-1.5">
                                                    {label(tab)}
                                                    {!tab.isBase ? (
                                                        <span
                                                            role="button"
                                                            tabIndex={0}
                                                            aria-label={t('pricelists.case-editor.remove-pricelist')}
                                                            className="text-grey-500 cursor-pointer rounded-full px-1 leading-none hover:bg-zinc-200"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                removeTab(tab.key);
                                                            }}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter' || event.key === ' ') {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();
                                                                    removeTab(tab.key);
                                                                }
                                                            }}
                                                        >
                                                            ✕
                                                        </span>
                                                    ) : null}
                                                </span>
                                            </LineTab>
                                        ))}
                                    </LineTabsList>
                                </LineTabs>
                                <Button
                                    {...buttonStyle({ variant: 'text', size: 'extraSmall' }, 'flex-none')}
                                    onClick={addTab}
                                >
                                    {t('pricelists.case-editor.add-pricelist')}
                                </Button>
                            </div>

                            <div className="flex flex-col pt-2 pr-9.5 pb-1">
                                <div className="flex items-center gap-2 text-[0.8rem]">
                                    <span className="text-grey-700 flex-1 font-semibold">
                                        {t('pricelists.case-editor.applies-from')}
                                    </span>
                                    {activeTab.isBase ? (
                                        <>
                                            <span className="text-grey-500 new:text-caption text-[0.65rem]">
                                                {t('pricelists.case-editor.case-opening-date')}
                                            </span>
                                            <span className="tabular-nums">
                                                {openedDate ? format(parseISO(openedDate), dateFormat) : '–'}
                                            </span>
                                        </>
                                    ) : (
                                        <DatePickerField
                                            format={dateFormat}
                                            value={toDate(activeTab.validFrom)}
                                            onChange={(date) =>
                                                updateActiveTab({
                                                    validFrom:
                                                        date && !isNaN(date.getTime())
                                                            ? format(date, DEFAULT_DATE_FORMAT_PATTERN)
                                                            : null,
                                                })
                                            }
                                            invalid={dateErrorText() !== null ? true : undefined}
                                            className="w-48"
                                        />
                                    )}
                                </div>
                                {dateErrorText() ? (
                                    <div className="new:text-caption self-end pt-0.5 text-[0.65rem] text-red-600">
                                        {dateErrorText()}
                                    </div>
                                ) : null}
                                {!activeTab.isBase ? (
                                    <div className="flex items-center gap-2 pt-1.5 text-[0.8rem]">
                                        <span className="text-grey-700 flex-1 font-semibold">
                                            {t('pricelists.case-editor.list-name')}
                                        </span>
                                        <Input
                                            className="bg-paper w-48 text-base md:text-xs"
                                            aria-label={t('pricelists.case-editor.list-name')}
                                            placeholder={t('pricelists.case-editor.list-name-placeholder')}
                                            maxLength={255}
                                            value={activeTab.name ?? ''}
                                            onChange={(event) => updateActiveTab({ name: event.target.value || null })}
                                        />
                                    </div>
                                ) : null}
                            </div>

                            {perEmployee ? null : (
                                <>
                                    <div className={cn(isLoadingPicker && 'pointer-events-none')}>
                                        <HourlyRatesEditForm
                                            layout="flow"
                                            isValidating={isValidating}
                                            hourlyRates={activeTab.rates}
                                            onChange={(rates) => updateActiveTab({ rates })}
                                            classifiers={classifiers}
                                            isLoading={isLoadingPicker}
                                        />
                                    </div>

                                    {/* Sits at the foot of the role grid, not by the pane heading: it empties the role
                                    prices of the open tab alone, leaving the other lists and this list's individual
                                    rates untouched. Aligned to the price column's edge. */}
                                    <div className="flex justify-end pr-9.5">
                                        <button
                                            type="button"
                                            className="text-link new:text-caption cursor-pointer border-none bg-inherit p-0 text-[0.72rem]"
                                            onClick={() =>
                                                updateActiveTab({
                                                    rates: activeTab.rates.map((rate) => ({
                                                        ...rate,
                                                        hourlyPrice: null,
                                                    })),
                                                })
                                            }
                                        >
                                            {t('pricelists.case-editor.clear-role-rates')}
                                        </button>
                                    </div>
                                </>
                            )}

                            <hr className="my-3 border-zinc-300/60" />

                            <PersonalRatesLedger
                                rows={activeTab.personalRates}
                                activeEmployees={activeEmployees}
                                caseTeamIds={caseTeamIds}
                                roleRates={activeTab.rates}
                                currencyTypeCode={currencyTypeCode}
                                isValidating={isValidating}
                                perEmployee={perEmployee}
                                onChange={(personalRates) => updateActiveTab({ personalRates })}
                            />

                            {perEmployee && link.copiedFrom ? (
                                <p className="text-grey-500 new:text-caption pt-3 text-[0.72rem] text-pretty">
                                    {t('pricelists.case-follow.own-hint-copied', { name: link.copiedFrom })}
                                </p>
                            ) : null}
                        </div>
                    )}
                </div>
            </div>
            <ModalFooter
                onCancel={onClose}
                onSave={props.mode === 'form' ? onApply : () => void onSave()}
                saveButtonTextId={props.mode === 'form' ? 'apply' : 'save'}
                disableSave={isSaving || isFetchingPickedFollow}
            />
        </>
    );
};
